// Notes as the apps store them (Markdown): the editor with its formatting bar, the read view with
// tappable checkboxes and place links, and a checkbox that flips on the click itself.
import { Link } from '@tanstack/react-router';
import { Bold, Heading, Italic, List, ListChecks, Strikethrough, Trash2 } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { cx } from '../kit/cx.ts';
import { Button } from '../kit/index.ts';
import t from '../kit/type.module.css';
import type { TripModel } from '../model/trip.ts';
import f from './Dialog.module.css';
import type { Me } from './data.ts';
import s from './Notes.module.css';

type Note = TripModel['notes'][number];

/** Wraps the selection in `marker` (or prefixes the line), like the apps' formatting bar. */
export function NoteEditor({
  model,
  me,
  note,
  onSave,
  onCancel,
  onDelete,
  fixedTitle,
}: {
  model: TripModel;
  me: Me;
  note?: Note;
  onSave: (title: string, body: string, personal: boolean) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => Promise<void>;
  /** A note with a set title (the day's "Journal"): no title field, never private. */
  fixedTitle?: string;
}) {
  const [title, setTitle] = useState(fixedTitle ?? note?.title ?? '');
  const [body, setBody] = useState(note?.body ?? '');
  const [personal, setPersonal] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const places = model.days.flatMap((d) => d.items).filter((p) => p.stopId);

  const apply = (change: (text: string, a: number, b: number) => { text: string; a: number; b: number }) => {
    const el = area.current;
    if (!el) return;
    const out = change(body, el.selectionStart, el.selectionEnd);
    setBody(out.text);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(out.a, out.b);
    });
  };
  const wrap = (marker: string) =>
    apply((text, a, b) => ({
      text: text.slice(0, a) + marker + text.slice(a, b) + marker + text.slice(b),
      a: a + marker.length,
      b: b + marker.length,
    }));
  const prefix = (marker: string) =>
    apply((text, a, b) => {
      const start = text.lastIndexOf('\n', a - 1) + 1;
      const line = text.slice(start);
      for (const m of ['- [ ] ', '- [x] ', '- ', '## '])
        if (line.startsWith(m)) {
          const rest = text.slice(0, start) + text.slice(start + m.length);
          return m === marker
            ? { text: rest, a: a - m.length, b: b - m.length }
            : {
                text: rest.slice(0, start) + marker + rest.slice(start),
                a: a - m.length + marker.length,
                b: b - m.length + marker.length,
              };
        }
      return {
        text: text.slice(0, start) + marker + text.slice(start),
        a: a + marker.length,
        b: b + marker.length,
      };
    });
  const link = (id: string, label: string) =>
    apply((text, a) => {
      const piece = `[${label}](pathfinder://item/${id}) `;
      return { text: text.slice(0, a) + piece + text.slice(a), a: a + piece.length, b: a + piece.length };
    });
  const tool = (label: string, Icon: typeof Bold, action: () => void) => (
    <button type="button" className={s.tool} aria-label={label} title={label} onClick={action}>
      <Icon size={16} />
    </button>
  );

  return (
    <form
      className={cx(s.card, s.editing)}
      onSubmit={(e) => {
        e.preventDefault();
        void onSave(title.trim(), body, personal);
      }}
    >
      {!fixedTitle && (
        <input
          aria-label="Note title"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={cx(f.input, t.titleL)}
        />
      )}
      <div className={s.toolbar} role="toolbar" aria-label="Formatting">
        {tool('Bold', Bold, () => wrap('**'))}
        {tool('Italic', Italic, () => wrap('*'))}
        {tool('Strikethrough', Strikethrough, () => wrap('~~'))}
        {tool('Heading', Heading, () => prefix('## '))}
        {tool('Bullet', List, () => prefix('- '))}
        {tool('Checkbox', ListChecks, () => prefix('- [ ] '))}
        {places.length > 0 && (
          <select
            aria-label="Link a place"
            className={cx(s.linkPick, t.caption)}
            value=""
            onChange={(e) => {
              const p = places.find((x) => x.item.id === e.target.value);
              if (p) link(p.item.id, p.item.title);
            }}
          >
            <option value="">Link a place…</option>
            {places.map((p) => (
              <option key={p.item.id} value={p.item.id}>{`${p.stopId} ${p.item.title}`}</option>
            ))}
          </select>
        )}
      </div>
      <textarea
        ref={area}
        aria-label="Note"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={10}
        className={cx(s.area, t.bodyS)}
      />
      {!note && !fixedTitle && (
        <label className={cx(f.check, t.bodyS)}>
          <input type="checkbox" checked={personal} onChange={(e) => setPersonal(e.target.checked)} /> Only me
          ({me.user.displayName || 'mine'})
        </label>
      )}
      <div className={f.actions}>
        {onDelete && (
          <Button variant="ghost" icon={Trash2} onClick={() => void onDelete()}>
            Delete
          </Button>
        )}
        <span className={f.spacer} />
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" type="submit">
          Save
        </Button>
      </div>
    </form>
  );
}

/** The apps' Markdown subset: headings, bullets, checkboxes (tappable), bold, italic, strike, links. */
export function Markdown({
  text,
  model,
  onToggle,
}: {
  text: string;
  model: TripModel;
  onToggle: (body: string) => void;
}) {
  const lines = text.split('\n');
  const toggle = (i: number) => {
    const next = [...lines];
    const line = next[i] ?? '';
    next[i] = line.startsWith('- [ ] ') ? `- [x] ${line.slice(6)}` : `- [ ] ${line.slice(6)}`;
    onToggle(next.join('\n'));
  };
  return (
    <div className={cx(s.body, t.bodyS)}>
      {lines.map((line, i) => {
        const key = `${i}:${line}`;
        if (/^- \[( |x|X)\] /.test(line)) {
          const checked = !line.startsWith('- [ ] ');
          return (
            <Toggle
              key={key}
              className={cx(s.check, checked && s.done)}
              checked={checked}
              onToggle={() => toggle(i)}
            >
              <span>{inline(line.slice(6), model)}</span>
            </Toggle>
          );
        }
        if (line.startsWith('- ') || line.startsWith('* '))
          return (
            <div key={key} className={s.bullet}>
              {inline(line.slice(2), model)}
            </div>
          );
        const h = /^#{1,3} (.*)$/.exec(line);
        if (h)
          return (
            <h4 key={key} className={cx(t.titleM, s.flat)}>
              {inline(h[1] ?? '', model)}
            </h4>
          );
        return line ? (
          <p key={key} className={s.flat}>
            {inline(line, model)}
          </p>
        ) : (
          <br key={key} />
        );
      })}
    </div>
  );
}

const INLINE = /\*\*(.+?)\*\*|~~(.+?)~~|\*(.+?)\*|\[([^\]]+)\]\(([^)\s]+)\)/;

function inline(text: string, model: TripModel): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;
  while (rest) {
    const m = INLINE.exec(rest);
    if (!m) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const k = n++;
    if (m[1] !== undefined) out.push(<strong key={k}>{inline(m[1], model)}</strong>);
    else if (m[2] !== undefined) out.push(<s key={k}>{inline(m[2], model)}</s>);
    else if (m[3] !== undefined) out.push(<em key={k}>{inline(m[3], model)}</em>);
    else {
      const label = m[4] ?? '';
      const href = m[5] ?? '';
      const ref = /^pathfinder:\/\/item\/([0-9a-f-]+)$/.exec(href)?.[1];
      const day = ref
        ? model.days.find((d) =>
            d.items.some((p) => p.item.id === ref || p.subItems.some((x) => x.item.id === ref)),
          )
        : undefined;
      if (ref)
        out.push(
          day ? (
            <Link key={k} to="/trips/$tripId" params={{ tripId: model.id }} search={{ day: day.id }}>
              {label}
            </Link>
          ) : (
            <span key={k}>{label}</span>
          ),
        );
      else if (/^https?:\/\//.test(href))
        out.push(
          <a key={k} href={href} target="_blank" rel="noreferrer">
            {label}
          </a>,
        );
      else out.push(label);
    }
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}

/** A checkbox that flips on the click itself, while the saved value catches up. */
export function Toggle({
  checked,
  onToggle,
  className,
  children,
}: {
  checked: boolean;
  onToggle: () => void;
  className: string | undefined;
  children: ReactNode;
}) {
  const [pending, setPending] = useState<boolean | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new saved value ends the pending one
  useEffect(() => setPending(null), [checked]);
  return (
    <label className={className}>
      <input
        type="checkbox"
        checked={pending ?? checked}
        onChange={() => {
          setPending(!(pending ?? checked));
          onToggle();
        }}
      />
      {children}
    </label>
  );
}
